import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    if (token) {
      // Verify token and get user profile
      authAPI.getProfile()
        .then(response => {
          setUser(response.data.data);
        })
        .catch(error => {
          console.error('Token verification failed:', error);
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password);
      const { token: newToken, user: userData } = response.data.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const loginWithMicrosoft = async () => {
    try {
      const response = await authAPI.getMicrosoftLoginUrl();
      const { authUrl } = response.data.data;
      
      // Redirect to Microsoft login
      window.location.href = authUrl;
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to initiate Microsoft login' 
      };
    }
  };

  const handleMicrosoftCallback = async (code) => {
    try {
      const response = await authAPI.microsoftCallback(code);
      const { token: newToken, user: userData } = response.data.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Microsoft authentication failed' 
      };
    }
  };

  const register = async (username, email, password, role = 'user') => {
    try {
      const response = await authAPI.register(username, email, password, role, token);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed' 
      };
    }
  };

  const isAdmin = () => {
    // Check if user has admin privileges through roles or permissions
    if (!user) return false;
    
    // Check simple role property first
    if (user.role === 'admin' || user.role === 'Super Admin') {
      return true;
    }
    
    // Check userRoles array (string array)
    if (user.userRoles && Array.isArray(user.userRoles)) {
      return user.userRoles.some(role => 
        role === 'Super Admin' || role?.toLowerCase() === 'admin'
      );
    }
    
    // Check roles array (object array with name property)
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.some(role => 
        role.name === 'Super Admin' || role.name?.toLowerCase() === 'admin'
      );
    }
    
    // Fallback: check if user has admin permissions
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions.some(permission => 
        permission.includes('admin') || permission.includes('user.')
      );
    }
    
    return false;
  };

  const isAuthenticated = () => {
    return !!user && !!token;
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    register,
    isAdmin,
    isAuthenticated,
    loginWithMicrosoft,
    handleMicrosoftCallback,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
